import React, { useState } from 'react';
import { useTransactions } from '../context/TransactionContext';
import { useSettings } from '../context/SettingsContext';
import { PieChart, BarChart } from '../components/Charts';
import { FinancialHealthScore, CashFlowForecast } from '../components/AnalyticsCharts';
import { exportToCSV, exportToExcel } from '../utils/exportUtils';
import { filterTransactionsByPeriod } from '../utils/dateFilters';

function Reports() {
  const { transactions, loading } = useTransactions();
  const { formatCurrency } = useSettings();
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  

  




  const getFilteredData = () => {
    return filterTransactionsByPeriod(transactions, selectedPeriod);
  };

  const getCategoryBreakdown = () => {
    const filteredTransactions = getFilteredData();
    const categoryTotals = {};
    filteredTransactions.filter(t => t.amount < 0).forEach(transaction => {
      const category = transaction.category;
      categoryTotals[category] = (categoryTotals[category] || 0) + Math.abs(transaction.amount);
    });
    return Object.entries(categoryTotals)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6);
  };

  const getMonthlyTrends = () => {
    const filteredTransactions = getFilteredData();
    const trendData = {};
    
    // Sort transactions by date
    const sortedTransactions = [...filteredTransactions].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    sortedTransactions.forEach(transaction => {
      const date = new Date(transaction.date);
      let key;
      
      if (selectedPeriod === 'week') {
        // For week: use day names (Mon, Tue, etc.)
        key = date.toLocaleDateString('en-US', { weekday: 'short' });
      } else if (selectedPeriod === 'month' || selectedPeriod === 'lastMonth') {
        // For month: use week numbers (Week 1, Week 2, etc.)
        const weekOfMonth = Math.ceil(date.getDate() / 7);
        key = `Week ${weekOfMonth}`;
      } else if (selectedPeriod === 'year') {
        // For year: use month names
        key = date.toLocaleDateString('en-US', { month: 'short' });
      } else {
        // For all time: use year-month
        key = date.toLocaleDateString('en-US', { year: '2-digit', month: 'short' });
      }
      
      if (!trendData[key]) {
        trendData[key] = { income: 0, expenses: 0 };
      }
      
      if (transaction.amount > 0) {
        trendData[key].income += transaction.amount;
      } else {
        trendData[key].expenses += Math.abs(transaction.amount);
      }
    });
    
    // Convert to sorted array for consistent ordering
    const sortedEntries = Object.entries(trendData);
    
    if (selectedPeriod === 'week') {
      // Sort by day of week
      const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      sortedEntries.sort(([a], [b]) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
    } else if (selectedPeriod === 'month' || selectedPeriod === 'lastMonth') {
      // Sort by week number
      sortedEntries.sort(([a], [b]) => {
        const weekA = parseInt(a.split(' ')[1]);
        const weekB = parseInt(b.split(' ')[1]);
        return weekA - weekB;
      });
    }
    
    return Object.fromEntries(sortedEntries);
  };

  const getTopExpenses = () => {
    const filteredTransactions = getFilteredData();
    return filteredTransactions
      .filter(t => t.amount < 0)
      .sort((a, b) => a.amount - b.amount)
      .slice(0, 5);
  };

  const categoryBreakdown = getCategoryBreakdown();
  const monthlyTrends = getMonthlyTrends();
  const topExpenses = getTopExpenses();
  const filteredTransactions = getFilteredData();
  const totalExpenses = filteredTransactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const totalIncome = filteredTransactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);

  if (loading && transactions.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px',
        fontSize: '1.125rem',
        color: '#6b7280'
      }}>
        Loading reports data...
      </div>
    );
  }

  if (!loading && transactions.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Reports & Analytics
          </h1>
          <p style={{ color: 'var(--gray-500)' }}>
            Financial insights and analytics for better money management
          </p>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
            No Transaction Data Available
          </h3>
          <p style={{ color: 'var(--gray-500)', marginBottom: '1.5rem' }}>
            Add some transactions to see detailed reports and analytics.
          </p>
          <a href="/transactions" className="btn btn-primary">
            Add Your First Transaction
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>
            Reports & Analytics
          </h1>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="form-input period-selector"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                onClick={() => exportToCSV(transactions)}
                className="btn btn-secondary"
                style={{ fontSize: '0.875rem' }}
              >
                CSV Export
              </button>
              <button 
                onClick={() => exportToExcel(transactions)}
                className="btn btn-secondary"
                style={{ fontSize: '0.875rem' }}
              >
                Excel Export
              </button>
            </div>
          </div>
        </div>
        <p style={{ color: 'var(--gray-500)' }}>
          Financial insights and analytics for better money management
        </p>
      </div>

  {/* Summary Cards */}
  <div className="auto-grid-min200" style={{ marginBottom: '2rem' }}>
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Total Income</span>
            <span className="stat-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="m16 6 2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg></span>
          </div>
          <div className="stat-value" style={{ color: 'var(--income-green)' }}>
            {formatCurrency(totalIncome)}
          </div>
          <div className="stat-change" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
            {selectedPeriod === 'all' ? 'All time total' : selectedPeriod === 'week' ? 'This week' : selectedPeriod === 'month' ? 'This month' : selectedPeriod === 'lastMonth' ? 'Last month' : 'This year'}
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Total Expenses</span>
            <span className="stat-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="m16 18 2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z"/></svg></span>
          </div>
          <div className="stat-value" style={{ color: 'var(--expense-red)' }}>
            {formatCurrency(totalExpenses)}
          </div>
          <div className="stat-change" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
            {selectedPeriod === 'all' ? 'All time total' : selectedPeriod === 'week' ? 'This week' : selectedPeriod === 'month' ? 'This month' : selectedPeriod === 'lastMonth' ? 'Last month' : 'This year'}
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Net Balance</span>
            <span className="stat-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg></span>
          </div>
          <div className="stat-value" style={{ color: totalIncome - totalExpenses >= 0 ? 'var(--income-green)' : 'var(--expense-red)' }}>
            {formatCurrency(totalIncome - totalExpenses)}
          </div>
          <div className="stat-change" style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
            {selectedPeriod === 'all' ? 'All time total' : selectedPeriod === 'week' ? 'This week' : selectedPeriod === 'month' ? 'This month' : selectedPeriod === 'lastMonth' ? 'Last month' : 'This year'}
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-title">Transactions</span>
            <span className="stat-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/></svg></span>
          </div>
          <div className="stat-value">{filteredTransactions.length}</div>
          <div className="stat-change">{filteredTransactions.length} transactions</div>
        </div>
      </div>

      {/* Advanced Analytics Section */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.125rem', fontWeight: '600' }}>
          Monthly Spending Analysis
        </h3>
        {categoryBreakdown.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
            No data available for analysis
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
            <BarChart 
              data={categoryBreakdown.slice(0, 6).map(([name, value]) => ({ name: name.length > 8 ? name.substring(0, 8) + '...' : name, value }))}
              width={Math.max(400, categoryBreakdown.length * 80)}
              height={250}
              formatCurrency={formatCurrency}
              responsive
            />
          </div>
        )}
      </div>

      {/* Charts Section */}
  <div className="two-col-grid large-gap" style={{ marginBottom: '2rem' }}>
        {/* Spending by Category */}
        <div className="card">
          <h3 style={{ marginBottom: '1.5rem', fontSize: '1.125rem', fontWeight: '600' }}>
            Spending by Category
          </h3>
          {categoryBreakdown.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
              No expense data for selected period
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <PieChart 
                data={categoryBreakdown.map(([name, value]) => ({ name, value }))}
                width={280}
                height={280}
                responsive
              />
            </div>
          )}
        </div>

        {/* Top Expenses */}
        <div className="card">
          <h3 style={{ marginBottom: '1.5rem', fontSize: '1.125rem', fontWeight: '600' }}>
            Largest Expenses
          </h3>
          {topExpenses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
              No expenses for selected period
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {topExpenses.map((expense, index) => (
                <div key={expense.id || index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fee2e2'  // Light red background like transaction page
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--expense-red)">
                        <path d="M7 10l5 5 5-5z"/>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                        {expense.description}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                        {expense.category}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '1rem' }}>
                    {formatCurrency(Math.abs(expense.amount))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cash Flow Forecast & Trends */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
          Cash Flow Forecast & Trends
        </h3>
        <CashFlowForecast
          data={Object.entries(monthlyTrends).map(([period, data]) => ({
            period,
            income: data.income || 0,
            expenses: Math.abs(data.expenses || 0)
          }))}
          forecast={[
            // Simple forecast based on averages - could be enhanced with ML
            ...Array.from({ length: 3 }, (_, i) => {
              const avgIncome = Object.values(monthlyTrends).reduce((sum, d) => sum + (d.income || 0), 0) / Object.keys(monthlyTrends).length;
              const avgExpenses = Object.values(monthlyTrends).reduce((sum, d) => sum + Math.abs(d.expenses || 0), 0) / Object.keys(monthlyTrends).length;
              
              const futureDate = new Date();
              futureDate.setMonth(futureDate.getMonth() + i + 1);
              
              return {
                period: futureDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                income: avgIncome,
                expenses: avgExpenses
              };
            })
          ]}
          height={300}
          formatCurrency={formatCurrency}
        />
      </div>

      {/* Category Breakdown Table */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.125rem', fontWeight: '600' }}>
          Category Breakdown
        </h3>
        {categoryBreakdown.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
            No category data for selected period
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600', color: 'var(--gray-700)' }}>Category</th>
                  <th style={{ textAlign: 'right', padding: '1rem', fontWeight: '600', color: 'var(--gray-700)' }}>Amount</th>
                  <th style={{ textAlign: 'right', padding: '1rem', fontWeight: '600', color: 'var(--gray-700)' }}>Percentage</th>
                  <th style={{ textAlign: 'center', padding: '1rem', fontWeight: '600', color: 'var(--gray-700)' }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {categoryBreakdown.map(([category, amount], index) => {
                  const percentage = totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0;
                  return (
                    <tr key={category} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '1rem', fontWeight: '500' }}>{category}</td>
                      <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>
                        {formatCurrency(amount)}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'right', color: 'var(--gray-600)' }}>
                        {percentage.toFixed(1)}%
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <span style={{ 
                          color: percentage > 25 ? 'var(--expense-red)' : 'var(--income-green)', 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          padding: '0.25rem 0.5rem',
                          background: percentage > 25 ? '#fee2e2' : '#dcfce7',
                          borderRadius: '12px'
                        }}>
                          {percentage > 25 ? 'High' : 'Normal'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Financial Health Analysis */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
          Financial Health Analysis
        </h3>
        <FinancialHealthScore
          metrics={{
            savingsRate: totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0,
            debtToIncome: 0, // Could be calculated from debt transactions
            expenseRatio: totalIncome > 0 ? (totalExpenses / totalIncome) * 100 : 0,
            emergencyFund: totalIncome > 0 ? Math.min(100, ((totalIncome - totalExpenses) * 6 / (totalExpenses * 6)) * 100) : 0
          }}
          height={200}
        />
      </div>

      {/* Spending Patterns Analysis */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.125rem', fontWeight: '600' }}>
          Spending Patterns & Predictions
        </h3>
  <div className="auto-grid-min200" style={{ marginBottom: '2rem' }}>
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', opacity: 0.9 }}>
              Daily Average Spending
            </h4>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
              {formatCurrency(totalExpenses / Math.max(1, selectedPeriod === 'week' ? 7 : selectedPeriod === 'month' ? 30 : selectedPeriod === 'quarter' ? 90 : 365))}
            </p>
          </div>
          
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', opacity: 0.9 }}>
              Projected Monthly Savings
            </h4>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
              {formatCurrency(Math.max(0, (totalIncome - totalExpenses) * (selectedPeriod === 'week' ? 4.33 : selectedPeriod === 'month' ? 1 : selectedPeriod === 'quarter' ? 0.33 : 0.083)))}
            </p>
          </div>
          
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', opacity: 0.9 }}>
              Budget Efficiency
            </h4>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
              {totalIncome > 0 ? Math.round(((totalIncome - totalExpenses) / totalIncome) * 100) : 0}%
            </p>
          </div>
          
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', opacity: 0.9 }}>
              Financial Health Score
            </h4>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
              {Math.min(100, Math.max(0, Math.round(((totalIncome - totalExpenses) / Math.max(totalIncome, 1)) * 100 + (categoryBreakdown.length > 0 ? 20 : 0))))}/100
            </p>
          </div>
        </div>
      </div>

      {/* Financial Insights */}
      <div className="card">
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
          Key Financial Insights
        </h3>
  <div className="auto-grid-min250" style={{ gap: '1.5rem' }}>
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Top Spending Category
            </h4>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>
              {categoryBreakdown.length > 0 ? categoryBreakdown[0][0] : 'No data'}
            </p>
            <p style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--expense-red)' }}>
              {categoryBreakdown.length > 0 ? formatCurrency(categoryBreakdown[0][1]) : formatCurrency(0)}
            </p>
          </div>
          
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Average Transaction
            </h4>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>
              Per transaction amount
            </p>
            <p style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--text-primary)' }}>
              {filteredTransactions.length > 0 ? formatCurrency(Math.abs(filteredTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0) / filteredTransactions.length)) : formatCurrency(0)}
            </p>
          </div>
          
          <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Savings Rate
            </h4>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>
              Income saved this period
            </p>
            <p style={{ fontSize: '1.25rem', fontWeight: '700', color: totalIncome > 0 ? 'var(--income-green)' : 'var(--gray-500)' }}>
              {totalIncome > 0 ? Math.round(((totalIncome - totalExpenses) / totalIncome) * 100) : 0}%
            </p>
          </div>
        </div>
        
        {/* Smart Recommendations */}
        <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Smart Recommendations
          </h4>
          <div className="auto-grid-min250">
            {categoryBreakdown.length > 0 && (
              <div style={{ padding: '1rem', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <h5 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                  Reduce {categoryBreakdown[0][0]} Spending
                </h5>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>
                  Save up to {formatCurrency(categoryBreakdown[0][1] * 0.15)} monthly
                </p>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--income-green)' }}>
                  Potential Annual Savings: {formatCurrency(categoryBreakdown[0][1] * 0.15 * 12)}
                </div>
              </div>
            )}
            
            <div style={{ padding: '1rem', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <h5 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                Emergency Fund Goal
              </h5>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>
                Target: 6 months of expenses
              </p>
              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: totalExpenses * 6 > (totalIncome - totalExpenses) * 12 ? 'var(--expense-red)' : 'var(--income-green)' }}>
                Need: {formatCurrency(Math.max(0, totalExpenses * 6 - Math.max(0, totalIncome - totalExpenses) * 12))}
              </div>
            </div>
            
            <div style={{ padding: '1rem', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <h5 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                Investment Opportunity
              </h5>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>
                Recommended monthly investment
              </p>
              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                {formatCurrency(Math.max(0, (totalIncome - totalExpenses) * 0.7))}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Reports;